import { FaceState } from './face';
import { PalForm } from './types';

export interface FormPalette {
    eye: string;
    accent: string;
    background: string;
}

export interface ResolvedFacePalette extends FormPalette {
    pupil: string;
    cheek: string;
}

export interface MoodShift {
    dh: number;
    ds: number;
    dl: number;
}

export const FORM_PALETTES: Record<PalForm, FormPalette> = {
    [PalForm.Baby]: { eye: '#ff5fa1', accent: '#ffd9c0', background: '#1a0a14' },
    [PalForm.CalmExplorer]: { eye: '#7affcd', accent: '#c4f0e0', background: '#0a1a14' },
    [PalForm.NightCruiser]: { eye: '#9b7dff', accent: '#4a2d80', background: '#0a061a' },
    [PalForm.SpiritDriver]: { eye: '#ff5a3c', accent: '#ffb05a', background: '#1a0a06' },
};

const NO_SHIFT: MoodShift = { dh: 0, ds: 0, dl: 0 };

export function formPalette(form: PalForm): FormPalette {
    return FORM_PALETTES[form] ?? FORM_PALETTES[PalForm.Baby];
}

export function moodShift(state: FaceState): MoodShift {
    switch (state) {
        case FaceState.EXCITED:
            return { dh: -10, ds: 0, dl: 5 };
        case FaceState.SUPER_EXCITED:
            return { dh: -18, ds: 10, dl: 8 };
        case FaceState.INTENSE:
            return { dh: -8, ds: 5, dl: 0 };
        case FaceState.NERVOUS:
            return { dh: 10, ds: -5, dl: 0 };
        case FaceState.COLD:
            return { dh: 20, ds: -5, dl: 5 };
        case FaceState.SCARED:
            return { dh: 0, ds: -40, dl: 0 };
        case FaceState.CHECK_ENGINE:
        case FaceState.DISCONNECTED:
            return { dh: 0, ds: -30, dl: -5 };
        default:
            return NO_SHIFT;
    }
}

export function paletteForFace(form: PalForm, state: FaceState): ResolvedFacePalette {
    const base = formPalette(form);
    const eye = applyMoodShift(base.eye, moodShift(state));
    return {
        ...base,
        eye,
        pupil: eye,
        cheek: applyMoodShift(base.accent, { dh: 0, ds: -10, dl: -2 }),
    };
}

export function applyMoodShift(hex: string, shift: MoodShift): string {
    const hsl = hexToHsl(hex);
    return hslToHex({
        h: wrapHue(hsl.h + shift.dh),
        s: clamp(hsl.s + shift.ds, 0, 100),
        l: clamp(hsl.l + shift.dl, 0, 100),
    });
}

interface Hsl {
    h: number;
    s: number;
    l: number;
}

function hexToHsl(hex: string): Hsl {
    const normalized = hex.replace('#', '');
    const r = parseInt(normalized.slice(0, 2), 16) / 255;
    const g = parseInt(normalized.slice(2, 4), 16) / 255;
    const b = parseInt(normalized.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    if (max === min) return { h: 0, s: 0, l: l * 100 };

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h: number;
    switch (max) {
        case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
        case g:
            h = (b - r) / d + 2;
            break;
        default:
            h = (r - g) / d + 4;
            break;
    }
    return { h: h * 60, s: s * 100, l: l * 100 };
}

function hslToHex({ h, s, l }: Hsl): string {
    const saturation = s / 100;
    const lightness = l / 100;
    const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const hp = h / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    const [r1, g1, b1] =
        hp < 1 ? [c, x, 0] :
        hp < 2 ? [x, c, 0] :
        hp < 3 ? [0, c, x] :
        hp < 4 ? [0, x, c] :
        hp < 5 ? [x, 0, c] :
        [c, 0, x];
    const m = lightness - c / 2;
    return `#${toHex(r1 + m)}${toHex(g1 + m)}${toHex(b1 + m)}`;
}

function toHex(v: number): string {
    return Math.round(clamp(v, 0, 1) * 255).toString(16).padStart(2, '0');
}

function clamp(v: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, v));
}

function wrapHue(h: number): number {
    return ((h % 360) + 360) % 360;
}
